import { useState, useEffect, useRef, useMemo } from 'react';
import { X, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '@/store/appStore';
import { createProject } from '@/db/projects';
import { organizeProjectsFromDatabase } from '@/utils/organizeProjects';
import { generateA11yId } from '@/utils/accessibility';
import { handleError } from '@/utils/errorHandler';
import { useOrganization } from '@/context/OrganizationContext';

interface CreateSpaceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateSpaceModal({ isOpen, onClose }: CreateSpaceModalProps) {
  const closeCreateSpace = useAppStore((state) => state.closeCreateSpace);
  const setSpaces = useAppStore((state) => state.setSpaces);
  const setSpacesLoading = useAppStore((state) => state.setSpacesLoading);
  const setSpacesInitialLoadDone = useAppStore((state) => state.setSpacesInitialLoadDone);
  const setFilters = useAppStore((state) => state.setFilters);
  const setViewMode = useAppStore((state) => state.setViewMode);
  const { currentOrg } = useOrganization();

  const handleCreateSpace = async (name: string, description?: string) => {
    try {
      await createProject(name, description, currentOrg?.id);
      // Refresh spaces after creation
      setSpacesLoading(true);
      const refreshedSpaces = await organizeProjectsFromDatabase(undefined, { forceRefresh: true, orgId: currentOrg?.id });
      setSpaces(refreshedSpaces);
      setSpacesLoading(false);
      setSpacesInitialLoadDone(true);

      // Small delay to ensure store is updated
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Select the newly created space using its ID
      const createdSpace = refreshedSpaces.find(s => s.name === name);
      setFilters({ path: createdSpace?.id || name }, { skipCloseItems: true });
      setViewMode('list');

      closeCreateSpace();
    } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nella creazione dello spazio' });
      throw error;
    }
  };
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Generate unique ID for modal title
  const titleId = useMemo(() => generateA11yId('create-space-modal-title'), []);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setIsCreating(false);
      setError(null);
      // Focus input after modal animation
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || isCreating) return;

    setIsCreating(true);
    setError(null);
    try {
      await handleCreateSpace(name.trim(), description.trim() || undefined);
      onClose();
    } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nella creazione del progetto' });
      // Extract error message
      let errorMessage = 'Errore durante la creazione del progetto';
      if (error instanceof Error) {
        errorMessage = error.message;
        // Extract user-friendly message from Supabase error
        if (errorMessage.includes('already exists')) {
          errorMessage = `Un progetto con il nome "${name.trim()}" esiste già. Scegli un nome diverso.`;
        } else if (errorMessage.includes('Space with name')) {
          const match = errorMessage.match(/Space with name "([^"]+)"/);
          if (match) {
            errorMessage = `Un progetto con il nome "${match[1]}" esiste già. Scegli un nome diverso.`;
          }
        }
      }
      setError(errorMessage);
      // Keep modal open on error
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
      onKeyDown={handleKeyDown}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md bg-surface-secondary border border-border-primary rounded-xl shadow-2xl animate-scale-in origin-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
          <h2 id={titleId} className="text-lg font-semibold text-text-primary">Crea nuovo progetto</h2>
          <button
            onClick={onClose}
            aria-label="Chiudi finestra di dialogo"
            className="p-1.5 rounded-md hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-primary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label
                htmlFor="space-name"
                className="block text-sm font-medium text-text-secondary mb-2"
              >
                Nome progetto
              </label>
              <input
                ref={inputRef}
                id="space-name"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null); // Clear error when user types
                }}
                placeholder="Inserisci il nome del progetto"
                className={clsx(
                  'w-full px-4 py-2.5 rounded-lg border transition-colors',
                  error
                    ? 'bg-surface-primary border-accent-rose text-text-primary placeholder-text-quaternary'
                    : 'bg-surface-primary border-border-primary text-text-primary placeholder-text-quaternary',
                  'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
                )}
                disabled={isCreating}
                autoFocus
              />
              {error && (
                <div className="mt-2 flex items-start gap-2 text-accent-rose">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="space-description"
                className="block text-sm font-medium text-text-secondary mb-2"
              >
                Descrizione
              </label>
              <textarea
                id="space-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Inserisci una descrizione del progetto (opzionale)"
                rows={3}
                className={clsx(
                  'w-full px-4 py-2.5 rounded-lg border transition-colors resize-none',
                  'bg-surface-primary border-border-primary text-text-primary placeholder-text-quaternary',
                  'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
                )}
                disabled={isCreating}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={!name.trim() || isCreating}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors',
                'bg-accent-violet hover:bg-accent-violet/90',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isCreating ? 'Creazione...' : 'Crea progetto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
